/**
 * How a user has arranged the Orders page. Saved on their profile
 * (zeecore GET/PUT /orders/v1/ui-preferences), so it follows them across
 * devices. Presentation only: nothing here changes a figure.
 *
 * The closed sets and the first-visit defaults mirror
 * zeecore/src/projections/orders-daily/OrdersUiPreferencesService.ts.
 */

/** The sections below the lane cards, in their default order. */
export const ORDERS_UI_SECTIONS = ['workspace', 'all_orders', 'trend'] as const;
export type OrdersUiSection = (typeof ORDERS_UI_SECTIONS)[number];

export const ORDERS_UI_SECTION_LABELS: Record<OrdersUiSection, string> = {
  workspace: 'Needs action',
  all_orders: 'All orders',
  trend: 'Trend',
};

/** 0 turns the warning off. */
export const ETA_WARNING_OPTIONS = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 0, label: 'Off' },
] as const;
export type EtaWarningMinutes = (typeof ETA_WARNING_OPTIONS)[number]['minutes'];

/** Unconfirmed by the client: their first estimate, still to be checked. */
export const DEFAULT_ETA_WARNING_MINUTES: EtaWarningMinutes = 60;

/** Which figure the three cards lead with; both are always shown, one large. */
export const CARD_HEADLINE_OPTIONS = ['open', 'percent'] as const;
export type CardHeadline = (typeof CARD_HEADLINE_OPTIONS)[number];
export const DEFAULT_CARD_HEADLINE: CardHeadline = 'percent';

export const CARD_HEADLINE_LABELS: Record<CardHeadline, string> = {
  open: 'Open count',
  percent: '% complete',
};

export interface OrdersUiPreferences {
  section_order: OrdersUiSection[];
  sections_open: Record<OrdersUiSection, boolean>;
  eta_warning_minutes: EtaWarningMinutes;
  card_headline: CardHeadline;
}

/** What GET returns; `saved: false` means this user has never saved a layout. */
export interface OrdersUiPreferencesResponse extends OrdersUiPreferences {
  saved: boolean;
  updated_at: string | null;
}

export interface OrdersUiPreferencesPatch {
  section_order?: OrdersUiSection[];
  sections_open?: Partial<Record<OrdersUiSection, boolean>>;
  eta_warning_minutes?: EtaWarningMinutes;
  card_headline?: CardHeadline;
}

/** First visit: needs-action and trend open, the full table collapsed. */
export function defaultOrdersUiPreferences(): OrdersUiPreferences {
  return {
    section_order: [...ORDERS_UI_SECTIONS],
    sections_open: { workspace: true, all_orders: false, trend: true },
    eta_warning_minutes: DEFAULT_ETA_WARNING_MINUTES,
    card_headline: DEFAULT_CARD_HEADLINE,
  };
}

export function applyOrdersUiPatch(
  base: OrdersUiPreferences,
  patch: OrdersUiPreferencesPatch,
): OrdersUiPreferences {
  return {
    section_order: patch.section_order ? [...patch.section_order] : [...base.section_order],
    sections_open: { ...base.sections_open, ...patch.sections_open },
    eta_warning_minutes: patch.eta_warning_minutes ?? base.eta_warning_minutes,
    card_headline: patch.card_headline ?? base.card_headline,
  };
}

/** Fold a later patch into an earlier one, so several quick changes save as one request. */
export function mergeOrdersUiPatches(
  earlier: OrdersUiPreferencesPatch,
  later: OrdersUiPreferencesPatch,
): OrdersUiPreferencesPatch {
  const merged: OrdersUiPreferencesPatch = { ...earlier, ...later };
  if (earlier.sections_open || later.sections_open) {
    merged.sections_open = { ...earlier.sections_open, ...later.sections_open };
  }
  return merged;
}

/** Move `section` one place up (-1) or down (+1); unchanged at either end. */
export function moveOrdersSection(
  order: OrdersUiSection[],
  section: OrdersUiSection,
  direction: -1 | 1,
): OrdersUiSection[] {
  const from = order.indexOf(section);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
